import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AccountDto, TreasuryAccountsResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney } from './money';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canManage: boolean;
  onOpenLedger: (accountCode: string) => void;
  onReconcile: (bankAccountId: string) => void;
  onSessionLost: () => void;
  /** حساب أستاذ جديد فُتح تلقائيًا — دليل الحسابات فى الشاشات الأخرى يُعاد تحميله. */
  onAccountsChanged?: () => void;
}

/** قيمة الاختيار التى تطلب حسابًا جديدًا بدل ربط حساب قائم. */
const AUTO_ACCOUNT = '__auto__';

type Kind = 'cashbox' | 'bank';

const ISSUE_LABELS: Record<string, string> = {
  ACCOUNT_NOT_FOUND: 'الحساب غير موجود',
  ACCOUNT_NOT_POSTABLE: 'الحساب يجب أن يكون طرفيًا نشطًا',
  TREASURY_ACCOUNT_SUBLEDGER: 'دفتر الحساب المساعد لا يناسب هذا النوع',
  TREASURY_BALANCE_NOT_ZERO: 'لا يُوقف والحساب برصيد — حوّل رصيده أولاً',
  ACCOUNT_CODE_REQUIRED: 'فتح الحساب التلقائى مطفأ فى الإعدادات — اختر حسابًا قائمًا',
  ACCOUNT_CODE_RANGE_EXHAUSTED: 'لا كود حر تحت هذا الحساب الأب',
  PARENT_ACCOUNT_UNAVAILABLE: 'الحساب الأب غير موجود فى دليل الشركة',
};

function problemText(caught: ProblemError): string {
  const issues = caught.problem.issues ?? [];
  if (issues.length > 0) {
    return issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ');
  }
  if (caught.problem.status === 409) return 'الحساب مربوط بكيان نشط آخر';
  return caught.problem.detail ?? caught.problem.title;
}

/**
 * الخزائن والحسابات البنكية: كل كيان مربوط بحساب أستاذ طرفى، ورصيده رصيد الحساب.
 * الإضافة تختار حسابًا قائمًا غير مربوط، أو «حساب جديد تلقائى» يفتحه الخادم تحت النقدية
 * أو البنوك بالكود التالى — إن سمح إعداد الشركة `treasury.autoCreateLedgerAccount` (ADR-0006).
 */
export default function TreasuryAccounts({
  companyId,
  currency,
  accounts,
  canManage,
  onOpenLedger,
  onReconcile,
  onSessionLost,
  onAccountsChanged,
}: Props) {
  const [data, setData] = useState<TreasuryAccountsResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [kind, setKind] = useState<Kind>('cashbox');
  const [nameAr, setNameAr] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [iban, setIban] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [formFailure, setFormFailure] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [autoAllowed, setAutoAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .companySettings(companyId)
      .then((response) => {
        if (cancelled) return;
        setAutoAllowed(response.settings.treasury.autoCreateLedgerAccount);
        if (response.settings.treasury.autoCreateLedgerAccount) setAccountCode(AUTO_ACCOUNT);
      })
      // الإعدادات تحسّن الاختيار الافتراضى فقط؛ فشل قراءتها يُبقى الربط اليدوى
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleError = useCallback(
    (caught: unknown, setter: (message: string) => void) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setter(
        caught instanceof ProblemError
          ? problemText(caught)
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api
      .treasuryAccounts(companyId)
      .then(setData)
      .catch((caught: unknown) => handleError(caught, setFailure));
  }, [companyId, handleError]);

  useEffect(load, [load]);

  // الحسابات المتاحة للربط: طرفية بالدفتر المناسب ولا كيان نشط عليها
  const freeAccounts = useMemo(() => {
    const subledger = kind === 'cashbox' ? 'cashbox' : 'bank_or_wallet';
    const linked = new Set(
      [...(data?.cashboxes ?? []), ...(data?.bankAccounts ?? [])]
        .filter((item) => item.isActive)
        .map((item) => item.accountCode),
    );
    return accounts.filter(
      (account) =>
        account.isPostable && account.subledger === subledger && !linked.has(account.code),
    );
  }, [accounts, data, kind]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setFormFailure(null);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!nameAr.trim() || !accountCode) return;
    setSubmitting(true);
    setFormFailure(null);
    try {
      const auto = accountCode === AUTO_ACCOUNT;
      const account = auto ? {} : { accountCode };
      if (kind === 'cashbox') {
        await api.createCashbox(companyId, idempotencyKey, { nameAr: nameAr.trim(), ...account });
      } else {
        await api.createBankAccount(companyId, idempotencyKey, {
          nameAr: nameAr.trim(),
          ...account,
          ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
          ...(iban.trim() ? { iban: iban.trim() } : {}),
        });
      }
      setNameAr('');
      setAccountCode(autoAllowed ? AUTO_ACCOUNT : '');
      if (auto) onAccountsChanged?.();
      setBankName('');
      setIban('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught, setFormFailure);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggle(target: Kind, id: string, isActive: boolean): Promise<void> {
    setBusyId(id);
    setFailure(null);
    try {
      setData(
        await api.setTreasuryActive(
          companyId,
          target === 'cashbox' ? 'cashboxes' : 'bank-accounts',
          id,
          isActive,
        ),
      );
    } catch (caught) {
      handleError(caught, setFailure);
    } finally {
      setBusyId(null);
    }
  }

  const statusCell = (isActive: boolean) => (
    <span className={'pill' + (isActive ? '' : ' bad')}>{isActive ? 'نشط' : 'موقوف'}</span>
  );
  const toggleButton = (target: Kind, id: string, isActive: boolean) =>
    canManage && (
      <button
        type="button"
        className="ghost"
        disabled={busyId === id}
        onClick={() => void toggle(target, id, !isActive)}
      >
        {isActive ? 'إيقاف' : 'تفعيل'}
      </button>
    );

  return (
    <>
      {failure && (
        <div className="panel">
          <p className="error">{failure}</p>
        </div>
      )}

      <div className="panel">
        <h2>الخزائن</h2>
        {data === null && !failure && <Loading />}
        {data?.cashboxes.length === 0 && <p className="sub">لا خزائن بعد.</p>}
        {data && data.cashboxes.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الخزينة</th>
                  <th>حساب الأستاذ</th>
                  <th>الرصيد ({currency})</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.cashboxes.map((box) => (
                  <tr key={box.id}>
                    <td>{box.nameAr}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpenLedger(box.accountCode)}
                      >
                        {box.accountCode} — {box.accountNameAr}
                      </button>
                    </td>
                    <td className="num-cell">{formatMoney(box.balance)}</td>
                    <td>{statusCell(box.isActive)}</td>
                    <td>{toggleButton('cashbox', box.id, box.isActive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <h2>الحسابات البنكية والمحافظ</h2>
        {data?.bankAccounts.length === 0 && <p className="sub">لا حسابات بنكية بعد.</p>}
        {data && data.bankAccounts.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الحساب</th>
                  <th>IBAN</th>
                  <th>حساب الأستاذ</th>
                  <th>رصيد الدفتر</th>
                  <th>آخر مطابقة</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.bankAccounts.map((bank) => (
                  <tr key={bank.id}>
                    <td>
                      {bank.nameAr}
                      {bank.bankName && <div className="label">{bank.bankName}</div>}
                    </td>
                    <td className="mono">{bank.iban ?? ''}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpenLedger(bank.accountCode)}
                      >
                        {bank.accountCode} — {bank.accountNameAr}
                      </button>
                    </td>
                    <td className="num-cell">{formatMoney(bank.balance)}</td>
                    <td>
                      {bank.lastReconciledDate ? (
                        <>
                          {bank.lastReconciledDate}
                          <div className="label">
                            <bdi dir="ltr">{formatMoney(bank.lastReconciledBalance ?? '0')}</bdi>
                          </div>
                        </>
                      ) : (
                        'لم يُطابق بعد'
                      )}
                    </td>
                    <td>{statusCell(bank.isActive)}</td>
                    <td>
                      <div className="panel-actions">
                        {bank.isActive && (
                          <button
                            type="button"
                            className="ghost"
                            onClick={() => onReconcile(bank.id)}
                          >
                            مطابقة
                          </button>
                        )}
                        {toggleButton('bank', bank.id, bank.isActive)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManage && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>إضافة</h2>
          <div className="form-row">
            <label>
              النوع
              <select
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as Kind);
                  setAccountCode(autoAllowed ? AUTO_ACCOUNT : '');
                  touched();
                }}
              >
                <option value="cashbox">خزينة</option>
                <option value="bank">حساب بنكى أو محفظة</option>
              </select>
              <Hint>
                «خزينة» للنقدية فى الدرج، و«بنكى أو محفظة» لحساب البنك أو فودافون كاش وما يشبهها.
              </Hint>
            </label>
            <label className="grow">
              الاسم
              <input
                value={nameAr}
                maxLength={200}
                onChange={(event) => {
                  setNameAr(event.target.value);
                  touched();
                }}
              />
            </label>
            <label className="grow">
              حساب الأستاذ
              <select
                value={accountCode}
                onChange={(event) => {
                  setAccountCode(event.target.value);
                  touched();
                }}
              >
                <option value="">— حساب غير مربوط —</option>
                {autoAllowed && (
                  <option value={AUTO_ACCOUNT}>
                    حساب جديد تلقائى تحت {kind === 'cashbox' ? 'النقدية' : 'البنوك والمحافظ'}
                  </option>
                )}
                {freeAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>
                الحساب فى دليل الحسابات الذى تُسجَّل فيه حركتها. اسأل محاسبك لو لم تعرف أيها.
              </Hint>
            </label>
          </div>
          {kind === 'bank' && (
            <div className="form-row">
              <label className="grow">
                اسم البنك
                <input
                  value={bankName}
                  maxLength={200}
                  onChange={(event) => {
                    setBankName(event.target.value);
                    touched();
                  }}
                />
              </label>
              <label className="grow">
                IBAN
                <input
                  value={iban}
                  dir="ltr"
                  maxLength={42}
                  onChange={(event) => {
                    setIban(event.target.value);
                    touched();
                  }}
                />
                <Hint>اختيارى — رقم الحساب الدولى من البنك، يبدأ بـ EG.</Hint>
              </label>
            </div>
          )}
          {accountCode === AUTO_ACCOUNT && (
            <p className="sub">
              يُفتح حساب أستاذ فرعى باسم الكيان بالكود التالى الحر تحت{' '}
              {kind === 'cashbox' ? 'النقدية (111000)' : 'البنوك والمحافظ (112000)'}.
            </p>
          )}
          {freeAccounts.length === 0 && !autoAllowed && (
            <p className="sub">
              لا حساب أستاذ متاح لهذا النوع، وفتح الحساب التلقائى مطفأ فى إعدادات الشركة.
            </p>
          )}
          {formFailure && <p className="error">{formFailure}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={submitting || !nameAr.trim() || !accountCode}
            >
              {submitting ? 'جارى الحفظ…' : 'إضافة'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
